import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ReactiveMap } from '@core/shared/util';
import { PlatformCatalog } from '@mp/content-manager/plattformkataloge/data-access';

import { IndustriekatalogeFacade } from './industriekataloge.facade';
import { PlattformkatalogeFacade } from './plattformkataloge.facade';

@Injectable()
export class PlattformKatalogeAktualisierungenFacade {
  readonly dirty$ = new ReactiveMap<string, boolean>({});
  readonly aktualisierungen$ = new ReactiveMap<string, number>({});
  readonly deletedAktualisierungen$ = new ReactiveMap<string, number>({});

  constructor(
    private readonly industriekatalogeFacade: IndustriekatalogeFacade,
    private readonly plattformkatalogeFacade: PlattformkatalogeFacade,
  ) {
    this.plattformkatalogeFacade.kataloge$.pipe(takeUntilDestroyed()).subscribe({
      next: (kataloge) => {
        this.initializeDirtyMapFrom(kataloge);
        this.initializeAktualisierungenMapFrom(kataloge);
        this.initializeDeletedAktualisierungenMapFrom(kataloge);
      },
    });

    this.industriekatalogeFacade.katalogPublished$.pipe(takeUntilDestroyed()).subscribe({
      next: ({ plattformkatalogId, markedTotal }) => {
        this.aktualisierungen$.set(plattformkatalogId, markedTotal);
      },
    });
  }

  private initializeDirtyMapFrom(kataloge: Array<PlatformCatalog>): void {
    const dirtyStates = kataloge.reduce((acc, katalog) => ({ ...acc, ...{ [katalog.catalogId]: false } }), {});
    this.dirty$.setMany(dirtyStates);
  }

  private initializeAktualisierungenMapFrom(kataloge: Array<PlatformCatalog>): void {
    const aktualisierungen = kataloge.reduce(
      (aktualisierungen, { catalogId, catalogsUpdatedAndReservedPublishCount }) => ({
        ...aktualisierungen,
        ...{ [catalogId]: catalogsUpdatedAndReservedPublishCount },
      }),
      {},
    );

    this.aktualisierungen$.setMany(aktualisierungen);
  }

  private initializeDeletedAktualisierungenMapFrom(kataloge: Array<PlatformCatalog>): void {
    const deletedAktualisierungen = kataloge.reduce(
      (aktualisierungen, { catalogId }) => ({
        ...aktualisierungen,
        ...{ [catalogId]: 0 },
      }),
      {},
    );
    this.deletedAktualisierungen$.setMany(deletedAktualisierungen);
  }

  deleteAktualisiertenKatalog(id: string, veroeffentlichungVorgemerkt: boolean, remove = true): void {
    const amount = remove ? 1 : -1;
    if (veroeffentlichungVorgemerkt) {
      const currentAktualisierteKataloge = this.aktualisierungen$.get(id) ?? 0;
      this.aktualisierungen$.set(id, currentAktualisierteKataloge - amount);
    }

    const deletedAktualisierungen = this.deletedAktualisierungen$.get(id) ?? 0;
    this.deletedAktualisierungen$.set(id, deletedAktualisierungen + amount);
  }

  markPlattformkatalogAsDirty(id: string): void {
    const currentDirtyState = this.dirty$.get(id);
    if (currentDirtyState === false) {
      this.dirty$.set(id, true);
    }
  }
}
